import { useCallback, useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoDisturbOutlinedIcon from "@mui/icons-material/DoDisturbOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { toast } from "react-toastify";
import api, { downloadFile } from "../../../BrandOnBoarding/api";
import { AuthContext } from "../../../context/auth-context";
import useHTMLReportHistory from "../../BrandAudit/HTMLReportTab/useHTMLReportHistory";
import CustomizedTables from "../../../Component/UI/Table/Table";
import { StyledTableCell, StyledTableRow } from "../../../Component/UI/Table/Table";
import { DiscoveryContext } from "../discovery-context";
import tabClasses from "../../BrandAudit/HTMLReportTab/HTMLReportTab.module.css";

const PAGE_SIZE = 5;

const TABLE_HEADERS = [
  { id: "brand", name: "Brand", sorting: false },
  { id: "category", name: "Category", sorting: false },
  { id: "llm", name: "LLM", sorting: false },
  { id: "market", name: "Market", sorting: false },
  { id: "persona", name: "Persona", sorting: false },
  { id: "date", name: "Date", sorting: false },
  { id: "status", name: "Status", sorting: false },
  { id: "actions", name: "Actions", sorting: false },
];

function formatDate(dateString) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function StatusChip({ status }) {
  if (status === "completed") return <Chip label="Completed" color="success" size="small" />;
  if (status === "failed") return <Chip label="Failed" color="error" size="small" />;
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CircularProgress size={12} thickness={5} />
      <Chip label="In Progress" color="warning" size="small" />
    </Box>
  );
}

function renderChips(items, max = 3) {
  if (!Array.isArray(items) || items.length === 0) return <span>—</span>;
  const visible = items.slice(0, max);
  const remaining = items.slice(max);
  return (
    <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
      {visible.map((item) => (
        <Box
          key={item}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            fontSize: "12px",
            fontWeight: 500,
            color: "#374151",
          }}
        >
          {item}
        </Box>
      ))}
      {remaining.length > 0 && (
        <Tooltip
          title={<Box>{remaining.map((r) => <div key={r}>{r}</div>)}</Box>}
          arrow
          placement="top"
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "#e5e7eb",
              fontSize: "12px",
              fontWeight: 700,
              color: "#4b5563",
              cursor: "help",
            }}
          >
            +{remaining.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function ValueBadge({ value }) {
  if (!value) return <span>—</span>;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.5,
        py: 0.5,
        borderRadius: "6px",
        border: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        fontSize: "12px",
        fontWeight: 500,
        color: "#374151",
      }}
    >
      {value}
    </Box>
  );
}

/** Row for an existing report job — no per-row regenerate */
function ReportJobRow({ item, onViewInline, onViewInNewTab, onDownload, onDelete, inlineViewLoading }) {
  const [downloading, setDownloading] = useState(false);
  const [viewingNew, setViewingNew] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isCompleted = item.status === "completed";
  const isInProgress = item.status === "in-progress" || item.status === "pending";
  const { selections = {} } = item;

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(item.jobId, item.transactionId); }
    finally { setDownloading(false); }
  };

  const handleOpenNew = async () => {
    setViewingNew(true);
    try { await onViewInNewTab(item.jobId); }
    finally { setViewingNew(false); }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDelete(item.jobId);
      setDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const market = selections.markets?.[0] || selections.market || "—";
  const llm = selections.llms?.[0] || selections.llm || "—";

  return (
    <>
      <StyledTableRow>
        <StyledTableCell><ValueBadge value={selections.brand} /></StyledTableCell>
        <StyledTableCell><ValueBadge value={selections.category} /></StyledTableCell>
        <StyledTableCell><ValueBadge value={llm} /></StyledTableCell>
        <StyledTableCell>
          <ValueBadge value={market === "GeneralMarket" ? "General Market" : market} />
        </StyledTableCell>
        <StyledTableCell>
          {Array.isArray(selections.persona) && selections.persona.length > 0
            ? renderChips(selections.persona, 3)
            : <ValueBadge value="General Profile" />}
        </StyledTableCell>
        <StyledTableCell title={formatDateTime(item.createdAt)}>
          {formatDate(item.createdAt)}
        </StyledTableCell>
        <StyledTableCell><StatusChip status={item.status} /></StyledTableCell>
        <StyledTableCell>
          <Box display="flex" alignItems="center" gap={0.5}>
            {isCompleted && (
              <>
                <Tooltip title="View inline">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={() => onViewInline(item.jobId)} disabled={inlineViewLoading}>
                    {inlineViewLoading ? <CircularProgress size={14} /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={handleOpenNew} disabled={viewingNew}>
                    {viewingNew ? <CircularProgress size={14} /> : <OpenInNewIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton size="small" className={tabClasses.actionIcon} onClick={handleDownload} disabled={downloading}>
                    {downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={isInProgress ? "Cancel generation" : "Delete report"}>
              <IconButton size="small" className={tabClasses.actionIconDanger} onClick={() => setDeleteDialog(true)}>
                {isInProgress ? <DoDisturbOutlinedIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </StyledTableCell>
      </StyledTableRow>

      {/* Delete / cancel confirm */}
      <Dialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isInProgress ? "Cancel Generation?" : "Delete Report?"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isInProgress
              ? "This will stop the report generation and remove it from history."
              : "This will permanently remove the report. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : isInProgress ? "Cancel Generation" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function DiscoveryReportingModal({ open, onClose, onViewInlineOpen }) {
  const { user } = useContext(AuthContext);
  const discoveryCtx = useContext(DiscoveryContext);
  const userId = user?.user_role?.user_id;
  const isAdmin =
    user?.user_role?.role?.name == "Company admin" ||
    user?.user_role?.role?.name == "Super User";
  const transactionId = discoveryCtx.state.transactionId;
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const brandName = discoveryCtx.entity;
  const discoveryType = discoveryCtx.type || "brand";

  const [generating, setGenerating] = useState(false);
  const [generatingJson, setGeneratingJson] = useState(false);
  const [page, setPage] = useState(1);
  const [newVersionDialog, setNewVersionDialog] = useState(false);
  const [inlineLoadingJobId, setInlineLoadingJobId] = useState(null);

  const {
    jobs,
    loading: historyLoading,
    addJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory,
  } = useHTMLReportHistory({ userId, transactionId });

  const totalPages = Math.ceil(jobs.length / PAGE_SIZE);
  const pagedJobs = jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inProgressCount = jobs.filter(
    (j) => j.status === "in-progress" || j.status === "pending"
  ).length;

  // ── Generate ───────────────────────────────────────────────────────────────

  const initiateGeneration = useCallback(
    async () => {
      setGenerating(true);
      setNewVersionDialog(false);
      const selections = {
        brand: brandName,
        category,
        llms: [llm],
        markets: [market],
        persona: [],
      };
      try {
        const res = await api.post("/llm-reporting/generateDiscoveryHTMLReport", {
          transactionId,
          userId,
          format: "html",
          type: discoveryType,
          selections,
        });
        const { jobId } = res.data;
        if (!jobId) throw new Error("No job ID received from server");
        addJob(jobId, selections);
        setPage(1);
        toast.info("Report generation started. Track the status in the table below.", {
          autoClose: 5000,
        });
      } catch (err) {
        const msg =
          err.response?.data?.error || err.message || "Failed to start report generation";
        toast.error(msg);
      } finally {
        setGenerating(false);
      }
    },
    [transactionId, userId, discoveryType, brandName, category, llm, market, addJob]
  );

  // ── Generate JSON ──────────────────────────────────────────────────────────

  const handleGenerateJson = useCallback(async () => {
    setGeneratingJson(true);
    const selections = {
      brand: brandName,
      category,
      llms: [llm],
      markets: [market],
      persona: [],
    };
    try {
      const filename = `${brandName || 'Brand'}_Discovery.json`;
      await downloadFile('/llm-reporting/getReportingJson', filename, {
        transactionId,
        userId,
        type: discoveryType,
        flag: 'discovery',
        selections,
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to download JSON';
      toast.error(msg);
    } finally {
      setGeneratingJson(false);
    }
  }, [transactionId, userId, discoveryType, brandName, category, llm, market]);

  // ── Inline viewer ──────────────────────────────────────────────────────────

  const handleViewInline = useCallback(async (jobId) => {
    setInlineLoadingJobId(jobId);
    try {
      const response = await api.get(`/llm-reporting/download/${jobId}`, {
        responseType: "text",
      });
      const html = response.data || "";

      const onSaveUpdate = async (newHtml) => {
        await api.patch(`/llm-reporting/html-report/${jobId}`, {
          html: newHtml,
          userId,
          transactionId,
        });
        await refreshHistory();
      };

      const onSaveNew = async (newHtml) => {
        await api.post("/llm-reporting/html-report", {
          html: newHtml,
          userId,
          transactionId,
          sourceJobId: jobId,
        });
        await refreshHistory();
        setPage(1);
      };

      onViewInlineOpen({ jobId, html, transactionId, userId, onSaveUpdate, onSaveNew });
      onClose();
    } catch {
      toast.error("Failed to load report. Please try again.");
    } finally {
      setInlineLoadingJobId(null);
    }
  }, [onViewInlineOpen, onClose, userId, transactionId, refreshHistory]);

  // ── View in new tab ────────────────────────────────────────────────────────

  const handleViewInNewTab = useCallback(
    async (jobId) => {
      try {
        await viewReport(jobId);
      } catch (err) {
        if (err?.message === "popup_blocked") {
          toast.warn("Popup blocked. Please allow popups for this site.");
        } else {
          toast.error("Failed to open report.");
        }
      }
    },
    [viewReport]
  );

  const hasJobs = jobs.length > 0;

  return (
    <>
      {/* ── Main Reporting Modal ── */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { mx: 2, my: 3, maxHeight: "calc(100% - 48px)" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="span">
              Reporting - {brandName}
            </Typography>
            {inProgressCount > 0 && (
              <Box className={tabClasses.inProgressBadge}>
                <CircularProgress size={10} thickness={5} />
                <span>{inProgressCount} generating…</span>
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  onClick={() => { refreshHistory(); setPage(1); }}
                  disabled={historyLoading}
                  className={tabClasses.actionIcon}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Info banner — only when reports exist */}
          {hasJobs && (
            <Alert
              severity="info"
              sx={{ borderRadius: 0, borderBottom: "1px solid #e5e7eb", fontSize: "13px" }}
            >
              HTML reports capture a full snapshot of this discovery. Use the table below to view,
              download or delete previous versions. Generate a new version any time using the button below.
            </Alert>
          )}

          {/* Table / empty state */}
          {historyLoading && !hasJobs ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !hasJobs ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
              sx={{ py: 6 }}
            >
              <Typography variant="body2" color="text.secondary">
                No reports generated yet for this discovery.
              </Typography>
              <Button
                variant="contained"
                onClick={initiateGeneration}
                disabled={generating || generatingJson}
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              >
                {generating ? "Generating…" : "Generate Report"}
              </Button>
              {isAdmin && (<Button
                variant="outlined"
                onClick={handleGenerateJson}
                disabled={generating || generatingJson}
                startIcon={generatingJson ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
              >
                {generatingJson ? "Downloading…" : "Generate JSON"}
              </Button>)}

            </Box>
          ) : (
            <CustomizedTables
              headers={TABLE_HEADERS}
              data={pagedJobs}
              elevation={0}
              size="medium"
              render={(item) => (
                <ReportJobRow
                  key={item.jobId}
                  item={item}
                  onViewInline={handleViewInline}
                  onViewInNewTab={handleViewInNewTab}
                  onDownload={downloadReport}
                  onDelete={cancelJob}
                  inlineViewLoading={inlineLoadingJobId === item.jobId}
                />
              )}
            />
          )}
        </DialogContent>

        {/* Footer — pagination + Generate New Version */}
        {hasJobs && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              borderTop: "1px solid #e5e7eb",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography className={tabClasses.resultsText}>
                Showing{" "}
                <strong>{Math.min((page - 1) * PAGE_SIZE + 1, jobs.length)}</strong>{" "}
                to <strong>{Math.min(page * PAGE_SIZE, jobs.length)}</strong> of{" "}
                <strong>{jobs.length}</strong>
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  shape="rounded"
                  size="small"
                  className={tabClasses.pagination}
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {isAdmin && (<Button
                variant="outlined"
                size="small"
                startIcon={generatingJson ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
                disabled={generating || generatingJson}
                onClick={handleGenerateJson}
              >
                {generatingJson ? "Downloading…" : "Generate JSON"}
              </Button>)}

              <Button
                variant="outlined"
                size="small"
                startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                disabled={generating || generatingJson}
                onClick={() => setNewVersionDialog(true)}
              >
                {generating ? "Generating…" : "Generate New Version"}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* ── Generate New Version confirm dialog ── */}
      <Dialog open={newVersionDialog} onClose={() => setNewVersionDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate New Version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will create a new HTML report version for <strong>{brandName}</strong>. Previous
            versions will remain in the history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setNewVersionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={initiateGeneration} disabled={generating}>
            {generating ? <CircularProgress size={16} /> : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
